import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconDownload, IconEdit, IconTrash, IconView } from '../components/icons';
import Pagination from '../components/Pagination';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadListCsv } from '../utils/csv';
import usePaginatedList from '../hooks/usePaginatedList';
import useCsvExport from '../hooks/useCsvExport';

// TS port of the source (Falah) app's pages/Facilities.jsx. The source's
// intro copy ("Physical spaces and rooms in the mosque") is genericized —
// VNPF is not a mosque.
interface FacilityRow {
  id: number;
  name: string;
  building: string | null;
  floor: string | null;
  capacity: number | null;
  status: string;
}

export function Facilities() {
  const { lists } = useConfig();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const filters = useMemo(
    () => ({ search: search || undefined, status: status || undefined }),
    [search, status]
  );
  const { data, loading, error, setPage, reload: load } = usePaginatedList<FacilityRow>('/facilities', filters);

  const remove = async (facility: FacilityRow) => {
    if (!(await confirmDelete(`Delete "${facility.name}"?`))) return;
    try {
      await api.delete(`/facilities/${facility.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const { exporting, exportCsv } = useCsvExport(() =>
    downloadListCsv<FacilityRow>(
      api,
      '/facilities',
      { search: search || undefined, status: status || undefined },
      [
        { label: 'Name', value: (r) => r.name },
        { label: 'Building', value: (r) => r.building ?? '' },
        { label: 'Floor', value: (r) => r.floor ?? '' },
        { label: 'Capacity', value: (r) => r.capacity ?? '' },
        { label: 'Status', value: (r) => labelFor(lists, 'facility_status', r.status) },
      ],
      'facilities.csv'
    )
  );

  return (
    <>
      <header className="page-header">
        <h1>Facilities</h1>
        <Link className="btn btn-primary" to="/resources/facilities/new">
          + Add facility
        </Link>
      </header>
      <p className="muted">Physical spaces and rooms available for use.</p>

      <div className="toolbar">
        <input type="search" placeholder="Search name…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {listOptions(lists, 'facility_status').map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
        <IconButton
          icon={IconDownload}
          label={exporting ? 'Exporting…' : 'Download CSV'}
          onClick={exportCsv}
          disabled={exporting}
        />
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Building / Floor</th>
                <th>Capacity</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={5} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((f) => (
                  <tr key={f.id}>
                    <td>
                      <strong>{f.name}</strong>
                    </td>
                    <td>{[f.building, f.floor].filter(Boolean).join(' · ') || '—'}</td>
                    <td>{f.capacity ?? '—'}</td>
                    <td>
                      <span className={`badge badge-${f.status === 'active' ? 'normal' : 'important'}`}>
                        {labelFor(lists, 'facility_status', f.status)}
                      </span>
                    </td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/resources/facilities/${f.id}`} />
                      <IconButton icon={IconEdit} label="Edit" to={`/resources/facilities/${f.id}/edit`} />
                      <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(f)} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="muted center">
                    No facilities found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
